'use client';

import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';

type PulseNotificationProps = ComponentProps<'div'> & {
  animated?: boolean;
  color?: string;
};

/**
 * PulseNotification is a visual indicator that displays a dot with an expanded pulse ring.
 * The pulse ring can optionally animate to draw attention to new or important items.
 * The parent component is responsible for positioning this indicator.
 *
 * @param animated - If true, the pulse ring animates. If false, shows static expanded ring. Defaults to true.
 * @param color - The background color class for the notification dot. Defaults to 'bg-accent-green-foreground'.
 * @returns HTMLDivElement with the pulse notification (parent must position it)
 */
function PulseNotification({ className, animated = true, color = 'bg-accent-green-foreground', ...props }: PulseNotificationProps) {
  return (
    <div className={twMerge('relative flex size-2 items-center justify-center', className)} {...props}>
      <span className={twMerge('absolute size-4 rounded-full opacity-20', color, animated && 'animate-ping [animation-duration:2s] size-3 opacity-50')} />
      <span className={twMerge('relative size-2 rounded-full', color)} />
    </div>
  );
}

export { PulseNotification };
